import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { History } from "lucide-react";
import { useScheduledShifts, useOpenShift, useShiftsInRange } from "../hooks";
import { DateRangeSelector } from "../components/DateRangeSelector";
import { ScheduledShiftsList } from "../components/ScheduledShiftsList";
import {
  getPeriodRange,
  toApiEnd,
  toApiStart,
  type PeriodRange,
} from "../utils/scheduledShiftRange";
import {
  filterUnscheduledShifts,
  mergeAndSortDisplayItems,
} from "../utils/unscheduledShiftAdapter";
import { useLeaveRequests } from "@/features/leave/hooks";
import { mergeAndSortTimelineItems } from "../utils/leaveRequestAdapter";
import { getCurrentPosition } from "@/shared/utils/location";
import type { ScheduledShift } from "../types";
import type { LeaveRequestDisplayItem } from "../types";
import { PatrolScanCard } from "@/features/patrol/components/PatrolScanCard";
import { LeaveRequestModal } from "@/features/leave/components/LeaveRequestModal";
import { config } from "@/lib/config";
import { LeaveRequestDetailsModal } from "../components/LeaveRequestDetailsModal";

/**
 * Landing page under `/shifts`. Shows scheduled (planned) shifts for the
 * authenticated employee alongside any unscheduled actual shifts (clock-ins
 * without a corresponding scheduled shift) in the selected date range.
 */
export function ScheduledShiftsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<PeriodRange>(() =>
    getPeriodRange(new Date(), "week")
  );
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [editingLeaveRequest, setEditingLeaveRequest] =
    useState<LeaveRequestDisplayItem | null>(null);
  const [detailsLeaveRequest, setDetailsLeaveRequest] =
    useState<LeaveRequestDisplayItem | null>(null);
  const [highlightedLeaveRequestId, setHighlightedLeaveRequestId] = useState<number | null>(null);
  const didHandleLeaveFocusRef = useRef(false);

  const { startDateIso, endDateIso } = useMemo(
    () => ({
      startDateIso: toApiStart(range),
      endDateIso: toApiEnd(range),
    }),
    [range]
  );

  const {
    data: scheduledShifts = [],
    isLoading: isLoadingScheduled,
    error: scheduledError,
  } = useScheduledShifts(startDateIso, endDateIso);

  const {
    data: actualShifts = [],
    isLoading: isLoadingActual,
  } = useShiftsInRange(startDateIso, endDateIso);

  const { data: openShift } = useOpenShift();
  const { data: leaveResponse } = useLeaveRequests();
  const leaveRequests = useMemo(
    () =>
      Array.isArray(leaveResponse)
        ? leaveResponse
        : leaveResponse?.requests ?? [],
    [leaveResponse]
  );

  // Build the unified list: scheduled shifts + unscheduled actual shifts
  const displayItems = useMemo(() => {
    // Include the open shift even if the date-range query doesn't capture it
    // (e.g. it started before the selected window or the backend doesn't filter).
    const actualShiftsWithOpen =
      openShift && !actualShifts.some((s) => s.id === openShift.id)
        ? [...actualShifts, openShift]
        : actualShifts;

    const unscheduled = filterUnscheduledShifts(actualShiftsWithOpen, scheduledShifts);
    const shiftItems = mergeAndSortDisplayItems(
      scheduledShifts,
      unscheduled,
      actualShiftsWithOpen
    );
    return mergeAndSortTimelineItems(
      shiftItems,
      leaveRequests,
      startDateIso,
      endDateIso,
      { openShiftId: openShift?.id ?? null }
    );
  }, [
    scheduledShifts,
    actualShifts,
    openShift,
    leaveRequests,
    startDateIso,
    endDateIso,
    openShift?.id,
  ]);

  const isLoading = isLoadingScheduled || isLoadingActual;
  const focusedLeaveRequestId = Number(searchParams.get("leaveRequestId"));
  const shouldOpenLeaveModal = searchParams.get("openLeaveModal") === "1";
  const shouldStartShiftFromHeader = searchParams.get("openStartShift") === "1";

  useEffect(() => {
    if (!Number.isFinite(focusedLeaveRequestId) || focusedLeaveRequestId <= 0) return;
    if (didHandleLeaveFocusRef.current) return;
    didHandleLeaveFocusRef.current = true;

    setHighlightedLeaveRequestId(focusedLeaveRequestId);
    window.setTimeout(() => {
      const element = document.getElementById(`leave-request-${focusedLeaveRequestId}`);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    window.setTimeout(() => {
      setHighlightedLeaveRequestId(null);
    }, 4000);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("leaveRequestId");
    setSearchParams(nextParams, { replace: true });
  }, [focusedLeaveRequestId, searchParams, setSearchParams]);

  useEffect(() => {
    if (!shouldOpenLeaveModal) return;
    setEditingLeaveRequest(null);
    setIsLeaveModalOpen(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("openLeaveModal");
    setSearchParams(nextParams, { replace: true });
  }, [shouldOpenLeaveModal, searchParams, setSearchParams]);

  useEffect(() => {
    if (!shouldStartShiftFromHeader) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("openStartShift");
    setSearchParams(nextParams, { replace: true });

    setIsRequestingLocation(true);
    getCurrentPosition()
      .then((location) => {
        navigate("/shifts/confirm-start", { state: { location } });
      })
      .finally(() => {
        setIsRequestingLocation(false);
      });
  }, [shouldStartShiftFromHeader, searchParams, setSearchParams, navigate]);

  const handleStartShift = async (_shift: ScheduledShift) => {
    setIsRequestingLocation(true);
    try {
      const location = await getCurrentPosition();
      navigate("/shifts/confirm-start", { state: { location } });
    } finally {
      setIsRequestingLocation(false);
    }
  };

  const handleEndShift = (_shift: ScheduledShift) => {
    navigate("/shifts/confirm-end");
  };

  const handleEditLeaveRequest = (request: LeaveRequestDisplayItem) => {
    setEditingLeaveRequest(request);
    setIsLeaveModalOpen(true);
  };

  const handleOpenPendingLeaveApproval = (request: LeaveRequestDisplayItem) => {
    if (request.status !== "pending") return;
    const baseUrl = config.app.leaveApprovalsUrl;
    if (!baseUrl) return;
    const separator = baseUrl.includes("?") ? "&" : "?";
    const url = `${baseUrl}${separator}leaveRequestId=${request.leave_request_id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleViewLeaveRequestDetails = (request: LeaveRequestDisplayItem) => {
    setDetailsLeaveRequest(request);
  };

  return (
    <div
      className="flex flex-col h-full min-h-0 w-full max-w-full min-w-0 overflow-x-hidden bg-gray-50"
      data-testid="scheduled-shifts-page"
    >
      <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
        <div className="px-4 pt-4 min-w-0">
          <DateRangeSelector value={range} onChange={setRange} />
        </div>

        {/* Patrol scan card — only shown when there is an open shift and patrol is configured */}
        <PatrolScanCard requireOpenShift className="px-4 mt-4 min-w-0" />

        <div className="px-4 mt-6 min-w-0">
          <ScheduledShiftsList
            shifts={displayItems}
            isLoading={isLoading}
            error={scheduledError}
            onStartShift={handleStartShift}
            isStartDisabled={isRequestingLocation}
            openShiftId={openShift?.id ?? null}
            onEndShift={handleEndShift}
            onEditLeaveRequest={handleEditLeaveRequest}
            onOpenPendingLeaveApproval={handleOpenPendingLeaveApproval}
            onViewLeaveRequestDetails={handleViewLeaveRequestDetails}
            highlightedLeaveRequestId={highlightedLeaveRequestId}
          />
        </div>

        <div className="px-4 mt-6 mb-8 min-w-0">
          <Link
            to="/shifts/history"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            data-testid="scheduled-shifts-history-link"
          >
            <History className="h-4 w-4" aria-hidden="true" />
            View shift history
          </Link>
        </div>

        <LeaveRequestModal
          open={isLeaveModalOpen}
          onOpenChange={setIsLeaveModalOpen}
          editingRequest={editingLeaveRequest}
        />
        <LeaveRequestDetailsModal
          open={detailsLeaveRequest != null}
          onOpenChange={(open) => {
            if (!open) setDetailsLeaveRequest(null);
          }}
          request={detailsLeaveRequest}
        />
      </div>
    </div>
  );
}
